/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from "react";
import { useMutation } from "react-query";
import { TCTeamsFxContext } from "../components/TCTeamsContext";
import { TeamsFxContext } from "../components/Context";
import {
  getResponseUsingResponseType,
  handleTcTeamsAPIError,
} from "../services/utility";
import { ApiRequest, UseMutationTypeQuery } from "../types";
import { TeamsTeamcenterLogger } from "teamsteamcenterobservability";
import { ErrorMessage } from "../types/CommonTypes";
import { Configuration } from "../services/configuration";
import { Constants } from "../common/constants";

/**
 * allows to call any APIS from the backend, with the authentication bearer token.
 *
 * @param request Api request object
 * @return {UseQueryResult} query result
 */
export function useApiMutation<TPayload = void, TResponse = void>(
  request: ApiRequest,
  logger: TeamsTeamcenterLogger
): UseMutationTypeQuery<TPayload, TResponse> {
  const tcTeamsContext = useContext(TCTeamsFxContext);
  const teamsContext = useContext(TeamsFxContext);
  let callKey = request.key ? request.key : request.url;
  callKey = callKey instanceof Array ? callKey : [callKey];

  // calling API
  const mutate = useMutation<TResponse, Error, TPayload, unknown>(
    callKey,
    (body: TPayload) =>
      callApiAsync(body, request, logger, teamsContext, tcTeamsContext),
    {
      onSuccess: () => {
        if (request.invalidateQuery) {
          tcTeamsContext?.queryClient?.invalidateQueries({
            predicate: (query) => {
              for (const key of query.queryKey as string[]) {
                const invalidate =
                  (request.invalidateQuery && request.invalidateQuery(key)) ||
                  false;
                if (invalidate) return true;
              }
              return false;
            },
          });
        }
      },
    }
  );

  return mutate;
}

export const callApiAsync = async (
  obj: unknown,
  request: ApiRequest,
  logger: TeamsTeamcenterLogger,
  teamsContext: any,
  tcTeamsContext: any
) => {
  const accessToken: any =
    await teamsContext?.teamsUserCredential?.getToken("");
  const logContext = logger.createContext({ actionId: request.action || "" });
  if (!accessToken || !request.url) {
    return null;
  }

  const isFormdata = obj instanceof FormData;

  // Get TenantId for logger
  if (
    typeof teamsContext?.userInfo !== "undefined" &&
    teamsContext?.userInfo !== null
  ) {
    let tenantId = teamsContext?.userInfo?.tenantId;
    if (tenantId !== null) {
      logger.setTenantId(tenantId);
    }
  }

  if (request.action) {
    logger.logInformation(request.action + "() called.", logContext);
  }

  const correlationId: string = logContext.requestId;
  const teamcenterConfiguration = Configuration.getValueFromLocalStorage(
    Constants.cacheTeamcenterCode
  );
  const configuration = Configuration.getValueFromLocalStorage(
    Constants.cacheConfiguration
  );
  const tcVersionParam = localStorage.getItem(Constants.cacheTcVersion);
  // preparing the headers
  const headers: Headers = new Headers();
  headers.append("Authorization", `Bearer ${accessToken.token}`);
  if (!isFormdata) {
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");
  }
  headers.append(
    "sessionId",
    tcTeamsContext?.teamcenter?.session?.sessionId || ""
  );
  headers.append("userId", tcTeamsContext?.teamcenter?.session?.userId || "");
  headers.append("appType", Constants.appType);
  headers.append("userAadOid", teamsContext?.userInfo?.objectId || "");
  headers.append("x-siemens-session-id", logContext.sessionId);
  headers.append("x-siemens-operation-id", logContext.actionId);
  headers.append("x-correlation-id", correlationId);
  if (configuration && configuration.tcVersion) {
    headers.append("tcVersion", configuration.tcVersion);
  } else if (tcVersionParam) {
    headers.append("tcVersion", tcVersionParam);
  } else {
    headers.append("tcVersion", "default");
  }

  if (teamcenterConfiguration && teamcenterConfiguration.siteId) {
    headers.append("siteId", teamcenterConfiguration.siteId);
  }

  const selectedSite = Configuration.getValueFromLocalStorage(
    Constants.cacheSelectedSite
  );

  if (selectedSite && selectedSite.appDeploymentId) {
    headers.append("appDeploymentId", selectedSite.appDeploymentId);
  }

  if (request.headers) {
    request.headers.forEach((header: string[]) => {
      headers.append(header[0], header[1]);
    });
  }

  const requestInit: RequestInit = {
    method: request.method ?? "GET",
    headers: headers,
  };

  if (
    request.method &&
    (request.method === "POST" || request.method === "PUT")
  ) {
    if (isFormdata) {
      requestInit.body = obj;
    } else {
      requestInit.body = JSON.stringify(obj);
    }
  }

  const response: Response = await fetch(request.url, requestInit);

  if (request.action && response) {
    logger.logInformation(request.action + "() completed.", logContext);
  }

  if (!response || response.status < 200 || response.status > 204) {
    const errorMessage: ErrorMessage = await handleTcTeamsAPIError(
      request.url,
      correlationId,
      response,
      logContext
    );
    tcTeamsContext?.failure.setError(errorMessage);
    return {
      response: response,
      error: errorMessage,
    };
  }

  return await getResponseUsingResponseType(request.returnType, response);
};
